export class WordPressHook {
    constructor() {
        let meta = document.getElementById(
            `jsidea-meta-config`,
        ) as HTMLScriptElement;
        if (!meta && window.top !== window.self) {
            try {
                meta = window.top.document.getElementById(
                    `jsidea-meta-config`,
                ) as HTMLScriptElement;
                if (!meta) {
                    throw new Error(
                        `Missing meta tag in window.top: #jsidea-meta-config`,
                    );
                }
            } catch (error) {
                console.error(error);
                return;
            }
        }

        if (
            window.top !== window.self &&
            !(window.top as any).jsidea &&
            window.jsidea
        ) {
            (window.top as any).jsidea = window.jsidea;
        }

        if (!meta) {
            console.error("Missing meta tag:", `#jsidea-meta-config`);
            return;
        }

        try {
            jsidea.wp.config = JSON.parse(meta.textContent);
        } catch (error) {
            jsidea.wp.config = {} as any;
            console.error("Could not parse meta config json", error);
            return;
        }

        jsidea.wp.aria = new jsidea.AriaManager();
        jsidea.wp.user = new jsidea.WpUser();
        jsidea.wp.playerSync = new jsidea.PlayerSync();
        jsidea.wp.animation = new jsidea.AnimationView();
        jsidea.wp.adminBar = new jsidea.AdminBar();
        jsidea.wp.link = new jsidea.LinkSupport();
        if (jsidea.wp.config.showAdminBar) {
            jsidea.wp.adminBar.setReady();
        }

        this.addMediaBreakpointDebug();
        this.addBlockTemplatesExport();
    }

    /**
     * Add an admin bar item that writes all Gutenberg templates/template parts
     * stored in the database into the active theme (local installations only).
     */
    private addBlockTemplatesExport() {
        const config = jsidea.wp.config;
        if (!config.isLocal || !config.blockTemplatesExportRestUrl) {
            return;
        }

        const defaultTooltip =
            "Gutenberg-Templates aus der Datenbank ins Theme speichern und aus der Datenbank löschen";
        const confirmMessage =
            "Alle Gutenberg-Templates und Template-Teile des aktiven Themes " +
            "werden aus der Datenbank ins Theme geschrieben (templates/ und parts/) " +
            "und anschließend aus der Datenbank gelöscht.\n\n" +
            "Erst danach verwendet WordPress die Theme-Dateien.\n\n" +
            "Fortfahren?";

        jsidea.wp.adminBar.add({
            key: "export-block-templates",
            icon: {
                default: "fal fa-file-export",
                progress: "fal fa-spinner fa-spin",
                success: "fal fa-check",
                warning: "fal fa-exclamation-triangle",
                errored: "fal fa-times",
            },
            tooltip: defaultTooltip,
            onClick: async (item) => {
                if (item.isBusy) {
                    return;
                }

                if (!window.confirm(confirmMessage)) {
                    return;
                }

                item.tooltip = defaultTooltip;
                item.state = "progress";

                try {
                    const response = await fetch(
                        config.blockTemplatesExportRestUrl,
                        {
                            method: "POST",
                            credentials: "same-origin",
                            headers: {
                                "X-WP-Nonce": config.restNonce ?? "",
                                Accept: "application/json",
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify({ delete: true }),
                        },
                    );

                    const data: any = await response
                        .json()
                        .catch((): null => null);

                    if (!response.ok) {
                        throw new Error(
                            data?.message ??
                                `HTTP error! status: ${response.status}`,
                        );
                    }

                    const written: string[] = data?.written ?? [];
                    const skipped: string[] = data?.skipped ?? [];
                    const deleted: string[] = data?.deleted ?? [];
                    const errors: string[] = data?.errors ?? [];
                    const notices: { message: string }[] =
                        data?.notices ?? [];

                    const lines = [
                        `Theme: ${data?.theme ?? "?"}`,
                        `Geschrieben: ${written.length}`,
                        `Unverändert: ${skipped.length}`,
                        `Aus DB gelöscht: ${deleted.length}`,
                    ];
                    if (errors.length) {
                        lines.push(`Fehler: ${errors.length}`);
                    }
                    if (notices.length) {
                        lines.push(`Hinweise (theme.json): ${notices.length}`);
                    }
                    item.tooltip = lines.join("\n");

                    console.groupCollapsed(
                        "[jsidea.core] [AdminBar] Block templates exported",
                    );
                    console.log("written", written);
                    console.log("skipped", skipped);
                    console.log("deleted", deleted);
                    if (errors.length) {
                        console.warn("errors", errors);
                    }
                    for (const notice of notices) {
                        console.info(notice.message);
                    }
                    console.groupEnd();

                    item.setState(
                        errors.length
                            ? "errored"
                            : notices.length
                              ? "warning"
                              : "success",
                        4000,
                    );
                } catch (error) {
                    console.error(
                        "[jsidea.core] [AdminBar] Block template export failed:",
                        error,
                    );
                    item.tooltip = `Export fehlgeschlagen: ${
                        error instanceof Error ? error.message : String(error)
                    }`;
                    item.setState("errored", 6000);
                }
            },
        });
    }

    /**
     * Add media breakpoint debug info to the admin bar
     */
    private addMediaBreakpointDebug() {
        jsidea.wp.adminBar.add({
            key: "media-breakpoints",
            onInit: (item) => {
                const sizes = ["xs", "s", "m", "l", "xl", "xxl"];

                const values: Record<string, { start: number; end: number }> =
                    {};
                for (const size of sizes) {
                    const varName = `--jsidea-bp--${size}`;
                    const value = getComputedStyle(document.documentElement)
                        .getPropertyValue(varName)
                        .trim();
                    let num = parseInt(value.replace("px", ""));
                    if (!isNaN(num)) {
                        values[size] = { start: num, end: 0 };
                    } else {
                        console.warn(
                            `Could not parse breakpoint value for size ${size}: ${value}`,
                        );
                    }
                }

                sizes.forEach((si, i) => {
                    const v = values[si];
                    if (i < sizes.length - 1) {
                        v.end = values[sizes[i + 1]].start - 1;
                    } else {
                        v.end = Infinity;
                    }
                });

                const resps: Record<
                    string,
                    { start: number; end: number; icon: string; label: string }
                > = {};
                resps["mobile"] = {
                    start: values["xs"].start,
                    end: values["s"].start - 1,
                    icon: "fal fa-mobile",
                    label: "Mobile",
                };
                resps["landscape"] = {
                    start: values["s"].start,
                    end: values["m"].start - 1,
                    icon: "fal fa-mobile fa-rotate-90",
                    label: "Landscape",
                };
                resps["tablet"] = {
                    start: values["m"].start,
                    end: values["l"].start - 1,
                    icon: "fal fa-tablet",
                    label: "Tablet",
                };
                resps["desktop"] = {
                    start: values["l"].start,
                    end: Infinity,
                    icon: "fal fa-desktop",
                    label: "Desktop",
                };

                let tooltip: string[] = [];
                for (const si of sizes) {
                    const v = values[si];
                    tooltip.push(
                        `${si.toUpperCase()}: ${v.start}px - ${
                            v.end === Infinity ? "∞" : v.end + "px"
                        }`,
                    );
                }

                for (const r in resps) {
                    const v = resps[r];
                    tooltip.push(
                        `${r.charAt(0).toUpperCase() + r.slice(1)}: ${
                            v.start
                        }px - ${v.end === Infinity ? "∞" : v.end + "px"}`,
                    );
                }

                item.tooltip = tooltip.join("\n");

                const modes = [
                    "breakpoints",
                    "compact",
                    "icon",
                    "text",
                ] as const;
                type Mode = (typeof modes)[number];
                let mode = item.retrieve<Mode>("mode") || "breakpoints";

                const updateText = () => {
                    let width = window.innerWidth;

                    let currentSize = "unknown";
                    for (const si of sizes) {
                        const v = values[si];
                        if (width >= v.start && width <= v.end) {
                            currentSize = si;
                            break;
                        }
                    }

                    let currentResp;
                    for (const r in resps) {
                        const v = resps[r];
                        if (width >= v.start && width <= v.end) {
                            currentResp = v;
                            break;
                        }
                    }

                    if (mode === "breakpoints") {
                        item.html = `${currentResp.label}`;
                    } else if (mode === "compact") {
                        item.html = `<span class="${currentResp.icon}" title="${currentResp.label}"></span> ${currentSize.toUpperCase()}`;
                    } else if (mode === "icon") {
                        item.html = `<span class="${currentResp.icon}"></span>`;
                    } else if (mode === "text") {
                        item.html = `${currentResp.label} | ${currentSize.toUpperCase()}`;
                    } else {
                        console.warn(
                            "Unknown mode for media breakpoint admin bar:",
                            mode,
                        );
                        item.html = `${currentResp.label}`;
                    }
                };

                updateText();

                item.onClick.add(() => {
                    let currentIndex = modes.indexOf(mode);
                    currentIndex = (currentIndex + 1) % modes.length;
                    mode = modes[currentIndex];
                    item.store("mode", mode);
                    updateText();
                });

                window.addEventListener("resize", () => {
                    updateText();
                });
            },
        });
    }

    public static get supported() {
        if (!document.body) {
            console.warn(
                "Document body is not available, cannot determine if WordPress environment is supported.",
            );
            return false;
        }

        return (
            document.body.classList.contains("jsidea-wp") ||
            document.body.classList.contains("wp-admin") ||
            document.body.classList.contains("block-editor-iframe__body")
        );
    }
}
